import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Use OPAQUE logo (no alpha channel) to prevent checkerboard on early load
const BUTTERFLY_LOGO = "/butterfly-logo-small-opaque.png";

type LaunchScreenProps = {
  /** Optional copy under the logo (kept minimal to avoid layout flashes) */
  subtitle?: string;
  /** When true, fades the splash out (use before routing) */
  fadingOut?: boolean;
  /** Optional retry handler (used for auth restore failures) */
  onRetry?: () => void;
  className?: string;
};

export const LaunchScreen = ({
  subtitle,
  fadingOut = false,
  onRetry,
  className,
}: LaunchScreenProps) => {
  return (
    <div
      data-testid="launch-screen"
      className={cn(
        // IMPORTANT: Keep the splash BACKDROP fully opaque during the fade.
        // We only fade the *content* so nothing behind the overlay can show through.
        "min-h-screen bg-background flex items-center justify-center",
        className
      )}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <div
        data-testid="launch-screen-fade"
        className={cn(
          "transition-opacity duration-200",
          fadingOut ? "opacity-0" : "opacity-100"
        )}
      >
        <div className="flex flex-col items-center gap-4 px-6">
          <div className="w-20 h-20 relative">
            {/* Solid background to prevent transparency artifacts */}
            <div
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: "hsl(var(--background, 0 0% 100%))" }}
              aria-hidden="true"
            />
            <img
              src={BUTTERFLY_LOGO}
              alt="LupusLogix"
              width={80}
              height={80}
              className="w-full h-full object-contain animate-butterfly-flutter relative z-10"
              loading="eager"
              fetchPriority="high"
            />
          </div>

          {subtitle ? (
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              {subtitle}
            </p>
          ) : null}

          {onRetry ? (
            <Button onClick={onRetry} className="mt-2">
              Retry
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
